<!-- eslint-disable vue/no-v-html -->
<template>
  <div v-if="photos.length" class="photo-viewer">
    <button
      v-show="!gridView && !onlyOnePhoto"
      class="left-arrow arrow"
      @click="decrementPhotos"
    >
      <x-icon
        icon="chevron-left"
        color="white"
        :width="iconSize"
        :height="iconSize"
      />
    </button>

    <div class="image-area">
      <header>
        <x-heading v-if="title" :size="isMobile ? '3' : '1'" class="title">
          {{ title }}
        </x-heading>
        <button class="close" @click="close">
          <x-icon
            icon="close"
            color="white"
            :width="iconSize"
            :height="iconSize"
          />
        </button>
      </header>

      <x-loading-spinner v-if="loading" class="loading-spinner" />

      <div v-if="gridView" class="grid view">
        <div
          v-for="(photo, index) in photos"
          :key="photo.id"
          class="image-container"
        >
          <figure class="figure">
            <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
            <img
              :class="[themeClass, { selected: currentIndex === index }]"
              :src="photo.url"
              :alt="photo.caption ?? ''"
              @load="imageLoaded"
              @error="(e) => imageErrored(photo.id, e)"
              @click="goFullScreen(index)"
            />
          </figure>
          <x-heading v-if="photo.didError" size="4" class="error-message">
            Photo failed to load.
          </x-heading>
        </div>
      </div>

      <div v-else class="full-size view">
        <figure class="figure">
          <img
            :key="activeImage.id"
            class="image"
            :alt="activeImage.caption ?? ''"
            :src="activeImage.url"
            @load="imageLoaded"
            @error="(e) => imageErrored(activeImage.id, e)"
          />
          <figcaption
            v-if="
              activeImage.caption || activeImage.creditText || activeImage.html
            "
            ref="photoCaption"
            class="caption-wrap"
          >
            <div v-if="activeImage.html" v-html="sanitize(activeImage.html)" />
            <x-body-text v-if="activeImage.caption">
              {{ activeImage.caption }}
            </x-body-text>
            <x-metadata-text v-if="activeImage.creditText">
              Photo: {{ activeImage.creditText }}
            </x-metadata-text>
          </figcaption>
        </figure>
        <x-heading v-if="activeImage.didError" size="4" class="error-message">
          Photo failed to load.
        </x-heading>
      </div>

      <footer>
        <button
          class="grid-button ys-text-button1"
          :class="gridView ? 'round' : ''"
          appearance-color="secondary-white"
          @click="toggleGridView"
        >
          <x-icon
            :icon="gridView ? 'chevron-down' : 'grid'"
            width="16"
            height="20"
          />
          <div v-show="!gridView" class="divider" />
          <x-body-text v-show="!gridView" class="photo-counter" weight="bold">
            {{ currentIndex + 1 }} / {{ totalNumPhotos }}
          </x-body-text>
        </button>
      </footer>
    </div>

    <button
      v-show="!gridView && !onlyOnePhoto"
      class="right-arrow arrow"
      @click="incrementPhotos"
    >
      <x-icon
        icon="chevron-right"
        color="white"
        :width="iconSize"
        :height="iconSize"
      />
    </button>
  </div>
</template>

<!-- eslint-disable vue/block-lang -->
<script>
/* eslint-disable vue/component-api-style */
import { reactive } from 'vue'
import { v4 as uuid } from 'uuid'
import DOMPurify from 'dompurify'

import xIcon from '@/components/shared/x-icon'
import xHeading from '@/components/shared/x-heading'
import xBodyText from '@/components/shared/x-body-text'
import xLoadingSpinner from '@/components/shared/x-loading-spinner'
import xMetadataText from '@/components/shared/x-metadata-text'

import { getThemeClasses } from '@/lib/theme-utils'

import placeholderImage from '@/assets/images/placeholder-image.png'

const STATE_DEFAULTS = {
  photos: [],
  title: '',
  activeImageIndex: 0,
}

const state = reactive({
  photos: STATE_DEFAULTS.photos,
  title: STATE_DEFAULTS.title,
  activeImageIndex: STATE_DEFAULTS.activeImageIndex,
})

/**
 * Pulls in photos and opens the viewer
 *
 * @param photos - array of photos
 * @param idToOpen - (optional) uuid of photo to open first
 * @param title - (optional) title for the viewer
 */
export function openPhotoViewer(photos, idToOpen = '', title = '') {
  // Set default error state for each photo
  const initializedPhotos = photos.map((photo, index) => {
    // set the active index if one was provided
    if (idToOpen === photo.id) {
      state.activeImageIndex = index
    }
    return {
      didError: false,
      id: photo?.uuid ?? uuid(),
      ...photo,
    }
  })

  state.photos = initializedPhotos
  state.title = title
}

/**
 * Clears the photos and closes the viewer
 */
export function closePhotoViewer() {
  state.photos = STATE_DEFAULTS.photos
  state.title = STATE_DEFAULTS.title
  state.activeImageIndex = STATE_DEFAULTS.activeImageIndex
}

export default {
  components: {
    xIcon,
    xHeading,
    xBodyText,
    xLoadingSpinner,
    xMetadataText,
  },
  inject: {
    theme: { default: 'hunt' },
  },
  data() {
    return {
      gridView: false,
      loading: false,
      loadedImages: 0,
      // eslint-disable-next-line vue/no-unused-properties
      imageError: false,
      photoSetIndex: 0,
      numberOfPhotosViewed: 0,
    }
  },
  computed: {
    themeClass() {
      return getThemeClasses(this.theme)
    },
    photos() {
      return state.photos
    },
    title() {
      return state.title
    },
    activeImage() {
      return state.photos[state.activeImageIndex]
    },
    currentIndex() {
      return state.activeImageIndex
    },
    totalNumPhotos() {
      return state.photos.length
    },
    isMobile() {
      // In pixels
      const mobileBreakpoint = 768
      return window.innerWidth <= mobileBreakpoint
    },
    iconSize() {
      return this.isMobile ? 35 : 50
    },
    onlyOnePhoto() {
      return this.totalNumPhotos === 1
    },
  },
  watch: {
    photos() {
      if (this.photos.length) {
        this.loading = true
        document.addEventListener('keydown', this.arrowKeyEventHandler)
      }
    },
    gridView() {
      if (this.gridView) {
        this.loading = true
      }
    },
    // FUTURE - watch window width??
  },
  methods: {
    close() {
      document.removeEventListener('keydown', this.arrowKeyEventHandler)

      // Reset indexes
      this.photoSetIndex = 0
      state.activeImageIndex = 0

      closePhotoViewer()
    },
    toggleGridView() {
      this.gridView = !this.gridView
    },
    goFullScreen(index) {
      state.activeImageIndex = index
      this.gridView = false
    },
    incrementPhotos() {
      this.loading = true

      // Set to beginning if at the end
      if (state.activeImageIndex === this.totalNumPhotos - 1) {
        state.activeImageIndex = 0
      } else {
        state.activeImageIndex += 1
      }
      this.numberOfPhotosViewed++
    },
    decrementPhotos() {
      this.loading = true

      // Set to the end if at the beginning
      if (state.activeImageIndex === 0) {
        state.activeImageIndex = this.totalNumPhotos - 1
      } else {
        state.activeImageIndex -= 1
      }

      this.numberOfPhotosViewed++
    },
    checkLoading() {
      // Close spinner and reset loading count if single image done in fullscreen
      // or all images done in grid view
      if (
        !this.gridView ||
        (this.gridView && this.loadedImages === this.photos.length)
      ) {
        this.loading = false
        this.loadedImages = 0
      }
    },
    imageLoaded() {
      this.loadedImages++
      this.checkLoading()
    },
    imageErrored(id, evt) {
      this.loadedImages++
      const erroredPhoto = this.photos.find((photo) => photo.id === id)
      erroredPhoto.didError = true

      evt.target.src = placeholderImage

      this.checkLoading()
    },
    arrowKeyEventHandler(e) {
      // if there's only one photo, don't do anything with arrow keys
      if (this.onlyOnePhoto) {
        return
      }

      switch (e.key) {
        case 'ArrowLeft':
          this.decrementPhotos()
          break
        case 'ArrowRight':
          this.incrementPhotos()
          break
        case 'ArrowUp':
          // FUTURE - figure out calculation
          break
        case 'ArrowDown':
          // FUTURE - figure out calculation
          break
        default:
          break
      }

      if (this.gridView) {
        this.loading = false
      }
    },
    sanitize: DOMPurify.sanitize,
  },
}
</script>

<style lang="scss" scoped>
@use 'sass:map';
@use '@/styles/_layer-map.scss';
@use '@/styles/system/_themes.scss';
@use '@/styles/system/_colors.scss';
@use '@/styles/system/_type.scss';
@use '@/styles/system/_breakpoints.scss';
@use '@/styles/system/_animation.scss';

$widthContainer: 80vh;
$heightContainer: 80vh;

.photo-viewer {
  background-color: colors.$Overlay;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  transition: opacity animation.$timing_250 animation.$ease_standard;
  z-index: layer-map.$z-index-level-modals;
  overflow-y: scroll;
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;

  .arrow {
    background-color: transparent;

    @include breakpoints.mobile {
      padding: 0;

      span {
        font-size: 0.8rem;
      }
    }

    &:hover,
    &:focus {
      background-color: transparent;
      border-color: transparent;
    }
  }

  .image-area {
    display: flex;
    flex-flow: column nowrap;
    position: relative;
    max-height: $heightContainer;
    max-width: $widthContainer;
    margin: 0 20px;
    flex: 0 2 100%;

    @include breakpoints.mobile {
      margin: 0;
      max-width: 50vh;
    }

    header {
      .title {
        display: inline-block;
        color: colors.$BaseWhite;
      }

      .close {
        float: right;
        background-color: transparent;

        @include breakpoints.mobile {
          padding-right: 0;
        }

        &:hover,
        &:focus {
          background-color: transparent;
          border-color: transparent;
        }
      }
    }

    .loading-spinner {
      position: absolute;
      left: 50%;
      z-index: 10;
      height: 40px;
      width: 40px;
    }

    .view {
      height: 100%;
      width: 100%;
    }

    .full-size {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        max-height: 70vh;
      }

      img,
      .figure {
        max-width: $widthContainer;
        object-fit: contain;
        border-radius: 20px;

        @include breakpoints.mobile {
          margin: 0;
          max-width: 50vh;
        }
      }

      .figure {
        display: flex;
        flex-direction: column;
        margin: unset;
      }

      .caption-wrap {
        background-color: colors.$BaseWhite;
        padding: 1em;
        margin-top: 30px;
        border-radius: 20px;
      }

      .error-message {
        position: absolute;
      }
    }

    .grid {
      display: flex;
      flex-flow: row wrap;
      justify-content: center;
      background-color: colors.$BaseWhite;
      border-radius: 20px;
      overflow-y: auto;
      padding: 20px;

      .image-container {
        position: relative;

        img {
          margin: 10px;
          height: 250px;
          max-width: 250px;
          cursor: pointer;
          border-radius: 10px;
          object-fit: cover;

          &.selected {
            @include themes.ApplyTheme using($themeMap) {
              box-shadow: 0 0 2px 6px map.get($themeMap, 'primary');
            }
          }

          &:hover {
            @include themes.ApplyTheme using($themeMap) {
              box-shadow: 0 0 0px 3px map.get($themeMap, 'primary');
            }
          }

          @include breakpoints.tablet {
            height: 150px;
            max-width: 150px;
          }
        }

        .error-message {
          position: absolute;
          top: 48%;
          left: 17%;
          text-align: center;

          @include breakpoints.tablet {
            top: 35%;
            left: 4%;
          }
        }
      }
    }

    footer {
      margin-top: 30px;
      display: flex;
      justify-content: center;

      .grid-button {
        color: var(--ys-color-text-primary);
        padding: 0 20px;
        height: 48px;
        border-radius: 100px;
        background-color: var(--ys-color-surface-base);
        padding: 0 20px;
        display: flex;
        align-items: center;
        justify-content: center;

        &.round {
          width: 48px;
        }

        .x-icon {
          line-height: 0;
          margin-right: 0;
        }

        .divider {
          border: 1px solid colors.$Grey30;
          height: 70%;
          margin: 0 10px;
        }
      }
    }
  }
}
</style>
